import { directSupabase } from '../lib/supabaseClient';

export type WindowGeneratorPayload = {
  root: unknown;
  w: number;
  h: number;
  opens?: Record<string, string>;
  /** Presentation state (external/internal colours, vents, handles, extenders, etc.). Extensible by design. */
  presentation?: Record<string, any>;
};

export type WindowGeneratorTemplate = {
  id: string;
  user_id: string;
  name: string;
  payload: WindowGeneratorPayload;
  preview_design_external_url?: string | null;
  preview_design_internal_url?: string | null;
  preview_presentation_external_url?: string | null;
  preview_presentation_internal_url?: string | null;
  preview_external_url?: string | null;
  preview_internal_url?: string | null;
  created_at: string;
};

/** Argument for Window Generator onSavedToLibrary: either a full template or { name, payload } to create one. */
export type OnSavedToLibraryArg = WindowGeneratorTemplate | { name: string; payload: WindowGeneratorPayload };

const MAX_RECENT_LIBRARY_TEMPLATES = 10;

function isPresetTemplatePayload(payload: WindowGeneratorPayload | null | undefined): boolean {
  return String((payload as any)?.presentation?.presetStorageKind || '').trim() === 'designer-preset-v1';
}

async function pruneOldLibraryTemplates(userId: string, keepTemplateId?: string): Promise<void> {
  const { data, error } = await directSupabase
    .from('window_generator_templates')
    .select('id, payload, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);

  const libraryTemplates = ((data || []) as Array<{ id: string; payload: WindowGeneratorPayload; created_at: string }>)
    .filter((row) => !isPresetTemplatePayload(row.payload));
  const protectedIds = new Set<string>(keepTemplateId ? [keepTemplateId] : []);
  const idsToDelete = libraryTemplates
    .filter((row) => !protectedIds.has(row.id))
    .slice(Math.max(0, MAX_RECENT_LIBRARY_TEMPLATES - protectedIds.size))
    .map((row) => row.id);

  if (!idsToDelete.length) return;

  const { error: deleteError } = await directSupabase
    .from('window_generator_templates')
    .delete()
    .eq('user_id', userId)
    .in('id', idsToDelete);

  if (deleteError) throw new Error(deleteError.message);
}

export const windowGeneratorTemplatesService = {
  async list(): Promise<WindowGeneratorTemplate[]> {
    const { data: auth } = await directSupabase.auth.getUser();
    const userId = auth.user?.id;
    if (!userId) return [];

    const { data, error } = await directSupabase
      .from('window_generator_templates')
      .select(
        'id, user_id, name, payload, preview_design_external_url, preview_design_internal_url, preview_presentation_external_url, preview_presentation_internal_url, preview_external_url, preview_internal_url, created_at'
      )
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw new Error(error.message);
    return ((data || []) as WindowGeneratorTemplate[])
      .filter((template) => !isPresetTemplatePayload(template.payload))
      .slice(0, MAX_RECENT_LIBRARY_TEMPLATES);
  },

  async create(name: string, payload: WindowGeneratorPayload): Promise<WindowGeneratorTemplate> {
    const { data: auth } = await directSupabase.auth.getUser();
    const userId = auth.user?.id;
    if (!userId) throw new Error('Not authenticated');

    const { data, error } = await directSupabase
      .from('window_generator_templates')
      .insert({ user_id: userId, name, payload })
      .select()
      .single();

    if (error) throw new Error(error.message);
    const created = data as WindowGeneratorTemplate;
    if (!isPresetTemplatePayload(payload)) {
      try {
        await pruneOldLibraryTemplates(userId, created.id);
      } catch (pruneError) {
        console.warn('Failed to prune old Window Generator library templates', pruneError);
      }
    }
    return created;
  },

  async update(id: string, name: string, payload: WindowGeneratorPayload): Promise<WindowGeneratorTemplate> {
    const { data: auth } = await directSupabase.auth.getUser();
    const userId = auth.user?.id;
    if (!userId) throw new Error('Not authenticated');

    const { data, error } = await directSupabase
      .from('window_generator_templates')
      .update({ name, payload })
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data as WindowGeneratorTemplate;
  },

  async listPresets(): Promise<WindowGeneratorTemplate[]> {
    const { data: auth } = await directSupabase.auth.getUser();
    const userId = auth.user?.id;
    if (!userId) return [];

    const { data, error } = await directSupabase
      .from('window_generator_templates')
      .select(
        'id, user_id, name, payload, preview_design_external_url, preview_design_internal_url, preview_presentation_external_url, preview_presentation_internal_url, preview_external_url, preview_internal_url, created_at'
      )
      .eq('user_id', userId)
      .contains('payload', { presentation: { presetStorageKind: 'designer-preset-v1' } })
      .order('created_at', { ascending: false });

    if (error) throw new Error(error.message);
    return (data || []) as WindowGeneratorTemplate[];
  },

  async createPreset(name: string, payload: WindowGeneratorPayload): Promise<WindowGeneratorTemplate> {
    const marked: WindowGeneratorPayload = {
      ...payload,
      presentation: {
        ...(payload?.presentation || {}),
        presetStorageKind: 'designer-preset-v1',
      },
    };
    return this.create(name, marked);
  },

  async updatePreset(id: string, name: string, payload: WindowGeneratorPayload): Promise<WindowGeneratorTemplate> {
    const marked: WindowGeneratorPayload = {
      ...payload,
      presentation: {
        ...(payload?.presentation || {}),
        presetStorageKind: 'designer-preset-v1',
      },
    };
    return this.update(id, name, marked);
  },

  async updatePreviews(
    id: string,
    urls: {
      designExternalUrl: string;
      designInternalUrl: string;
      presentationExternalUrl: string;
      presentationInternalUrl: string;
    }
  ): Promise<WindowGeneratorTemplate> {
    const { data: auth } = await directSupabase.auth.getUser();
    const userId = auth.user?.id;
    if (!userId) throw new Error('Not authenticated');

    const { data, error } = await directSupabase
      .from('window_generator_templates')
      .update({
        preview_design_external_url: urls.designExternalUrl,
        preview_design_internal_url: urls.designInternalUrl,
        preview_presentation_external_url: urls.presentationExternalUrl,
        preview_presentation_internal_url: urls.presentationInternalUrl,
        // Legacy columns: keep in sync (use presentation as the "best" default)
        preview_external_url: urls.presentationExternalUrl,
        preview_internal_url: urls.presentationInternalUrl,
      })
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data as WindowGeneratorTemplate;
  },

  async delete(id: string): Promise<void> {
    const { data: auth } = await directSupabase.auth.getUser();
    const userId = auth.user?.id;
    if (!userId) throw new Error('Not authenticated');

    const { error } = await directSupabase
      .from('window_generator_templates')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);

    if (error) throw new Error(error.message);
  },
};
